import { useState } from 'react';
import { API_BASE } from '../lib/api';

interface GateItem {
  title: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WAIVED'; // FT-GATE.A2 — sync with API GateItem.status
  reason?: string;
  actionRequired?: string;
}

interface GateOverrideModalProps {
  projectId: string;
  phaseNumber: number;
  phaseName: string;
  failingItems: GateItem[];
  direction?: 'ENTRY' | 'EXIT';
  onClose: () => void;
  onOverrideSuccess: () => void;
}

export default function GateOverrideModal({
  projectId,
  phaseNumber,
  phaseName,
  failingItems,
  direction = 'ENTRY',
  onClose,
  onOverrideSuccess,
}: GateOverrideModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = reason.trim().length >= 20;

  const handleOverride = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(
        `${API_BASE}/projects/${projectId}/phases/${phaseNumber}/gate/override`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: reason.trim(), direction }),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Override failed (${res.status})`);
      }

      onOverrideSuccess();
    } catch (err: any) {
      setError(err.message ?? 'Override failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      // zIndex 70 sits above the HelpPopover panel (zIndex 60) so a
      // gate-failure tooltip left open by the user does not overlay
      // this modal in the brief window before the popover's outside-
      // click handler closes it.
      style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid rgba(245,158,11,0.4)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          width: '100%', maxWidth: 520,
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: 28, paddingBottom: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 20 }}>⚠</span>
          <div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
              color: 'var(--warning)',
            }}>
              Override Gate — {phaseName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              This will bypass the failing {direction === 'EXIT' ? 'exit' : 'entry'} gate checks and allow {direction === 'EXIT' ? 'handoff' : 'entry into the phase'}.
            </div>
          </div>
        </div>

        {/* Failing items */}
        {failingItems.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
              color: 'var(--text-secondary)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8,
            }}>
              Failing Items ({failingItems.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {failingItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(245,158,11,0.06)',
                    border: '1px solid rgba(245,158,11,0.2)',
                    fontSize: 13,
                    color: 'var(--text)',
                  }}
                >
                  <span style={{ color: 'var(--warning)', fontWeight: 700, marginRight: 6 }}>✗</span>
                  {item.title}
                  {item.actionRequired && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, marginLeft: 18 }}>
                      {item.actionRequired}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reason textarea */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            color: 'var(--text)', display: 'block', marginBottom: 6,
          }}>
            Override Reason <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(required)</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain why this gate override is necessary (min 20 characters)..."
            rows={4}
            style={{
              width: '100%',
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--surface-alt)',
              color: 'var(--text)',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 11,
            color: reason.trim().length >= 20 ? 'var(--text-secondary)' : 'var(--warning)',
            marginTop: 4, textAlign: 'right',
          }}>
            {reason.trim().length} / 20 min
          </div>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: 'var(--radius-sm)',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', fontSize: 12, marginBottom: 12,
          }}>
            {error}
          </div>
        )}
        </div>

        {/* Actions — always visible */}
        <div style={{ flexShrink: 0, display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '16px 28px 28px', borderTop: '1px solid var(--border)' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
              padding: '8px 16px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleOverride}
            disabled={!isValid || submitting}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
              padding: '8px 16px', borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: isValid ? 'var(--warning)' : 'var(--surface-alt)',
              color: isValid ? 'white' : 'var(--text-secondary)',
              cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Overriding...' : 'Confirm Override'}
          </button>
        </div>
      </div>
    </div>
  );
}
